// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getPageStateByType } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonPresentationProps } from "@synd/MobiusOverrides/Button/Button";
import { HEADER_HEIGHT_CSS_VAR } from "@synd/XDL/constants";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface OwnProps {
    relatedProductType?: string;
    anchorId?: string;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const pageId = getPageStateByType(state, "ProductDetailsPage").value?.id;
    if (pageId) {
        const widgets = getWidgetsByPageId(state, pageId);
        const carouselId = widgets.find(widget => {
            const hasSameCarouselType = Object.values(widget.contextualFields?.carouselType || {}).find(
                carouselType => carouselType === "relatedProducts",
            );

            if (hasSameCarouselType) {
                // check if has same related product type
                const hasSameRelatedProductType =
                    !!Object.values(widget.contextualFields.relatedProductType || {}).find(
                        relatedProductType => relatedProductType === props.relatedProductType,
                    ) || widget.fields.relatedProductType === props.relatedProductType;
                return hasSameRelatedProductType;
            }
            return hasSameCarouselType;
        })?.id;

        const products = carouselId ? getProductsForProductInfoList(state, carouselId) || [] : [];

        return {
            products,
        };
    }
    return {
        products: [],
    };
};

export interface SimilarItemsButtonStyles {
    wrapper?: InjectableCss;
    productsWrapper?: InjectableCss;
    button?: ButtonPresentationProps;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const similarItemsButtonStyles: SimilarItemsButtonStyles = {
    button: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-block: 12px;
        `,
    },
};

const styles = similarItemsButtonStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const SimilarItemsButton: FC<Props> = ({ products, anchorId }) => {
    const goToAnchor = () => {
        const selector = document.querySelector(`#${anchorId}`);
        if (!selector) {
            return;
        }

        const cssRoot = document.querySelector(":root") as HTMLElement;
        const headerHeight = parseInt(getComputedStyle(cssRoot).getPropertyValue(HEADER_HEIGHT_CSS_VAR) || "0", 10);

        const elementPosition = selector.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

        window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
        });
    };

    if (products.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Button variant="secondary" onClick={goToAnchor} {...styles.button}>
                {translate("View Similar Items")}
            </Button>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(SimilarItemsButton);
